import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getUserProfile } from '../config/firebase';
import { User as AppUser } from '../types/user';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<AppUser | null>(null);
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const profile = await getUserProfile(firebaseUser.uid);
        if (profile) {
          setUser(profile as AppUser);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    function handleClickOutsideMobile(event: MouseEvent) {
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        setMobileDropdownOpen(false);
      }
    }
    if (mobileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideMobile);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideMobile);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideMobile);
    };
  }, [mobileDropdownOpen]);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    navigate('/');
  };

  return (
    <header id="header" className="bg-white/80 backdrop-blur-lg sticky top-0 z-50 shadow-sm">
      <nav className="container mx-auto px-6 py-4 flex justify-between items-center max-w-7xl">
        <a href="/" className="text-xl font-bold text-teal-700">
          Healthier Smile Initiatives
        </a>
        <div className="hidden md:flex items-center space-x-6">
          <Link to="/story" className="nav-link text-stone-600 hover:text-teal-600 transition-colors">Our Story</Link>
          <Link to="/#strategy" className="nav-link text-stone-600 hover:text-teal-600 transition-colors">Our Strategy</Link>
          <Link to="/#challenges" className="nav-link text-stone-600 hover:text-teal-600 transition-colors">The Challenges</Link>
          <Link to="/#join-us" className="nav-link text-stone-600 hover:text-teal-600 transition-colors">Join Us</Link>
        </div>
        <div className="flex items-center space-x-4">
          <Link to="/donate" className="hidden md:block bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors shadow">
            Donate
          </Link>
          {/* User Dropdown - only on md+ screens */}
          {user && (
            <div className="relative hidden md:block" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen((open) => !open)}
                className="flex items-center space-x-2 px-3 py-1 rounded hover:bg-stone-100 transition-colors border border-stone-200"
              >
                <span className="text-teal-700 font-semibold text-sm">{user.fullName || user.email}</span>
                <svg className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white border border-stone-200 rounded shadow-lg z-50">
                  <ul className="py-1">
                    {user.role === 'Admin' && (
                      <>
                        <li>
                          <Link to="/admin/users" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setDropdownOpen(false)}>Users</Link>
                        </li>
                        <li>
                          <Link to="/admin/volunteers" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setDropdownOpen(false)}>Volunteers</Link>
                        </li>
                        <li>
                          <Link to="/admin/pledges" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setDropdownOpen(false)}>Pledges</Link>
                        </li>
                        <li>
                          <Link to="/admin/settings" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setDropdownOpen(false)}>Settings</Link>
                        </li>
                      </>
                    )}
                    <li>
                      <button onClick={() => { setDropdownOpen(false); handleLogout(); }} className="w-full text-left px-4 py-2 text-stone-700 hover:bg-stone-100">Logout</button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <button id="mobile-nav-button" className="md:hidden text-stone-700" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7"></path></svg>
        </button>
      </nav>
      <div id="mobile-nav" className={`${isMobileMenuOpen ? '' : 'hidden'} md:hidden px-6 pt-2 pb-4 space-y-2`}>
        <Link to="/story" className="block nav-link text-stone-600 hover:text-teal-600 transition-colors">Our Story</Link>
        <Link to="/#strategy" className="block nav-link text-stone-600 hover:text-teal-600 transition-colors">Our Strategy</Link>
        <Link to="/#challenges" className="block nav-link text-stone-600 hover:text-teal-600 transition-colors">The Challenges</Link>
        <Link to="/#join-us" className="block nav-link text-stone-600 hover:text-teal-600 transition-colors">Join Us</Link>
        <Link to="/donate" className="block mt-2 bg-teal-600 text-white text-center px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors shadow">
          Donate
        </Link>
        {/* User Dropdown - only on mobile (md:hidden) */}
        {user && (
          <div className="relative mt-4 md:hidden" ref={mobileDropdownRef}>
            <button
              onClick={() => setMobileDropdownOpen((open) => !open)}
              className="flex items-center w-full justify-between px-4 py-2 rounded bg-stone-100 text-teal-700 font-semibold text-sm"
            >
              <span>{user.fullName || user.email}</span>
              <svg className={`w-4 h-4 transition-transform ${mobileDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {mobileDropdownOpen && (
              <div className="absolute left-0 right-0 mt-2 bg-white border border-stone-200 rounded shadow-lg z-50">
                <ul className="py-1">
                  {user.role === 'Admin' && (
                    <>
                      <li>
                        <Link to="/admin/users" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setMobileDropdownOpen(false)}>Users</Link>
                      </li>
                      <li>
                        <Link to="/admin/volunteers" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setMobileDropdownOpen(false)}>Volunteers</Link>
                      </li>
                      <li>
                        <Link to="/admin/pledges" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setMobileDropdownOpen(false)}>Pledges</Link>
                      </li>
                      <li>
                        <Link to="/admin/settings" className="block px-4 py-2 text-stone-700 hover:bg-stone-100" onClick={() => setMobileDropdownOpen(false)}>Settings</Link>
                      </li>
                    </>
                  )}
                  <li>
                    <button onClick={() => { setMobileDropdownOpen(false); handleLogout(); }} className="w-full text-left px-4 py-2 text-stone-700 hover:bg-stone-100">Logout</button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;